import {FC, useEffect, useMemo, useState} from 'react'
import axios from 'axios'
import {Link, useNavigate, useSearchParams} from 'react-router-dom'
import {API_URL, brandName, toAbsoluteUrl} from '../../../../_metronic/helpers'
import {AppLoading} from '../../../../_metronic/helpers/components/AppLoading'
import {useAuth} from '../core/Auth'
import {AuthModel} from '../core/_models'
import './SupportSsoCallback.css'

type SupportSsoState =
  | {status: 'loading'; message: string}
  | {status: 'success'; message: string}
  | {status: 'error'; message: string}

type SupportSsoParams = {
  accountId: string
  userId: string
  fullName: string
  timestamp: string
  signature: string
  companyId: string
  version: string
  nonce: string
  permission: string
}

const exchangeRequests = new Map<string, Promise<AuthModel>>()

const readSupportSsoParams = (searchParams: URLSearchParams): SupportSsoParams | undefined => {
  const accountId = (searchParams.get('accountId') || '').trim()
  const userId = (searchParams.get('userId') || '').trim()
  const fullName = (searchParams.get('fullName') || '').trim()
  const timestamp = (searchParams.get('ts') || '').trim()
  const signature = (searchParams.get('sig') || '').trim()
  const companyId = (searchParams.get('companyId') || '').trim()
  const version = (searchParams.get('version') || '').trim()
  const nonce = (searchParams.get('nonce') || '').trim()
  const permission = (searchParams.get('permission') || '').trim()

  if (
    !/^\d+$/.test(accountId) ||
    !/^\d+$/.test(userId) ||
    !/^\d+$/.test(timestamp) ||
    !/^\d+$/.test(companyId) ||
    !/^[a-f0-9]{64}$/i.test(signature) ||
    version !== 'v2' ||
    !/^[a-zA-Z0-9_-]{32,128}$/.test(nonce) ||
    permission !== 'CustomerPanelReadOnlyAccess' ||
    !fullName ||
    fullName.length > 150
  ) {
    return undefined
  }

  return {accountId, userId, fullName, timestamp, signature, companyId, version, nonce, permission}
}

const getSupportSsoError = (error: any, timestamp?: string) => {
  const requestTime = Number(timestamp || 0) * 1000
  const isExpired = requestTime > 0 && Date.now() - requestTime > 2 * 60 * 1000

  if (isExpired) {
    return 'زمان استفاده از لینک ورود تمام شده است. لطفاً از CRM یک لینک جدید باز کنید.'
  }

  if (!error?.response) {
    return `ارتباط با سرور ${brandName} برقرار نشد. اینترنت خود را بررسی و دوباره تلاش کنید.`
  }

  if (error.response.status === 401 || error.response.status === 403) {
    return 'لینک ورود معتبر نیست یا منقضی شده است. لطفاً از CRM دوباره وارد شوید.'
  }

  if (error.response.status === 404) {
    return `کسب‌وکار مرتبط با این سازمان در ${brandName} پیدا نشد.`
  }

  if (error.response.status === 409) {
    return 'این لینک قبلاً استفاده شده است. لطفاً از CRM لینک جدیدی باز کنید.'
  }

  if (error.response.status === 503) {
    return 'ورود پشتیبانی روی سرور مقصد تنظیم نشده است. با مدیر سامانه تماس بگیرید.'
  }

  return 'ورود پشتیبانی انجام نشد. لطفاً لینک جدیدی از CRM باز کنید.'
}

const SupportSsoCallback: FC = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const {saveAuth, setCurrentUser} = useAuth()
  const [state, setState] = useState<SupportSsoState>({
    status: 'loading',
    message: 'در حال بررسی دسترسی پشتیبانی…',
  })
  const params = useMemo(() => readSupportSsoParams(searchParams), [searchParams])

  useEffect(() => {
    if (!params) {
      setState({
        status: 'error',
        message: 'اطلاعات لینک ورود ناقص یا نامعتبر است. لطفاً لینک را دوباره از CRM باز کنید.',
      })
      return
    }

    let active = true
    let redirectTimer: number | undefined
    const exchangeKey = `${params.version}|${params.accountId}|${params.companyId}|${params.userId}|${params.timestamp}|${params.nonce}|${params.signature}`
    let exchangeRequest = exchangeRequests.get(exchangeKey)
    if (!exchangeRequest) {
      exchangeRequest = axios.post<AuthModel>(`${API_URL}/support-sessions/exchange`, {
        version: params.version,
        accountId: Number(params.accountId),
        companyId: Number(params.companyId),
        userId: Number(params.userId),
        fullName: params.fullName,
        ts: Number(params.timestamp),
        nonce: params.nonce,
        permission: params.permission,
        sig: params.signature,
      }).then((response) => response.data)
      exchangeRequests.set(exchangeKey, exchangeRequest)
      exchangeRequest.then(
        () => window.setTimeout(() => exchangeRequests.delete(exchangeKey), 1000),
        () => window.setTimeout(() => exchangeRequests.delete(exchangeKey), 1000)
      )
    }

    exchangeRequest
      .then((auth) => {
        if (!active) return
        if (!auth?.tk || !auth?.cpi || auth?.support?.session_type !== 'support') {
          throw new Error('Invalid support SSO response')
        }

        saveAuth(auth)
        setCurrentUser(auth)
        setState({status: 'success', message: 'ورود امن انجام شد؛ در حال انتقال به داشبورد…'})
        redirectTimer = window.setTimeout(() => navigate('/dashboard', {replace: true}), 350)
      })
      .catch((error) => {
        if (!active) return
        setState({status: 'error', message: getSupportSsoError(error, params.timestamp)})
      })

    return () => {
      active = false
      if (redirectTimer) window.clearTimeout(redirectTimer)
    }
  }, [navigate, params, saveAuth, setCurrentUser])

  if (state.status === 'loading' || state.status === 'success') {
    return (
      <AppLoading
        mode='fullscreen'
        message={state.message}
        detail={
          state.status === 'success'
            ? 'کسب‌وکار مقصد در حال آماده‌سازی است'
            : 'اعتبار لینک و دسترسی شما در حال کنترل است'
        }
      />
    )
  }

  return (
    <main className='support-sso-page'>
      <section className='support-sso-card' role='alert' aria-labelledby='supportSsoTitle'>
        <img src={toAbsoluteUrl('/media/logos/default.svg')} alt={brandName} />
        <span className='support-sso-card__icon' aria-hidden='true'>
          <i className='las la-exclamation-triangle' />
        </span>
        <h1 id='supportSsoTitle'>ورود پشتیبانی انجام نشد</h1>
        <p>{state.message}</p>
        <div className='support-sso-card__actions'>
          <button type='button' className='btn btn-primary' onClick={() => window.close()}>
            <i className='las la-times' /> بستن این صفحه
          </button>
          <Link className='btn btn-light' to='/auth/login'>
            ورود معمولی به {brandName}
          </Link>
        </div>
        <small>برای امنیت، لینک‌های ورود پشتیبانی فقط مدت کوتاهی معتبر هستند.</small>
      </section>
    </main>
  )
}

export {SupportSsoCallback}
